import React, { useState, useRef, useEffect } from 'react';
import { FaEllipsisV, FaStar, FaShoppingCart, FaShare, FaFlag, FaBan, FaChevronRight, FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '@/utils/api';
import { useAppSelector } from '@/redux/Store';
import { REPORT_CATEGORIES } from '@/constants/reportCategories';
import { ProfileListing } from './ProfileCard';
import { formatName } from '@/utils/formatName';
import { getCachedShareUrl, prefetchShareToken, copyToClipboard } from '@/utils/shareLink';
import './ProfileActionsMenu.scss';

interface ProfileActionsMenuProps {
  profile: ProfileListing;
  onBuyClick: (profile: ProfileListing) => void;
  onSpotlightClick: (profile: ProfileListing) => void;
  onShareClick?: (profile: ProfileListing) => void;
}

type View = 'menu' | 'report-category' | 'report-reason' | 'report-details' | 'block-confirm';

const ProfileActionsMenu: React.FC<ProfileActionsMenuProps> = ({
  profile,
  onBuyClick,
  onSpotlightClick,
  onShareClick,
}) => {
  const currentUser = useAppSelector((state) => state.auth.user);
  const isOwnProfile = currentUser?.id === profile.creatorId;

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<View>('menu');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setView('menu');
    setSelectedCategory('');
    setSelectedReason('');
    setDetails('');
  };

  const handleToggle = () => {
    if (isOpen) {
      handleClose();
    } else {
      setView('menu');
      setIsOpen(true);
      // Warm the share-token cache so the eventual Share click can
      // copy synchronously (#256). Doing the mint here means the
      // click handler doesn't have to `await` before calling
      // `navigator.clipboard.writeText`, which Safari rejects once
      // the user-activation gate has closed.
      prefetchShareToken({ contentType: 'profile', contentId: profile.creatorUsername });
    }
  };

  const handleShare = () => {
    if (onShareClick) {
      onShareClick(profile);
      handleClose();
      return;
    }
    // Synchronous URL build — returns the cached token URL when
    // available, falls back to the bare URL otherwise. Clipboard
    // write happens in the same tick as the click via
    // `copyToClipboard`, which handles the modern API + a textarea
    // fallback for non-secure-context environments.
    const url = getCachedShareUrl({ contentType: 'profile', contentId: profile.creatorUsername });
    copyToClipboard(url).then(
      () => toast.success('Profile link copied!'),
      (err) => {
        console.error('[share] copy failed:', err);
        toast.error('Failed to copy link');
      },
    );
    handleClose();
  };

  const handleBlock = async () => {
    setIsSubmitting(true);
    try {
      await api.post(`/users/${profile.creatorId}/block`);
      toast.success(`${formatName({ displayName: profile.creatorDisplayName, username: profile.creatorUsername })} has been blocked`);
      handleClose();
    } catch {
      toast.error('Failed to block user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReportSubmit = async () => {
    setIsSubmitting(true);
    try {
      await api.post(`/users/${profile.creatorId}/report`, {
        category: selectedCategory,
        reason: selectedReason || undefined,
        details: details.trim() || undefined,
      });
      toast.success('Report submitted. Thank you for keeping Klyx safe.');
      handleClose();
    } catch {
      toast.error('Failed to submit report');
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeCategoryData = REPORT_CATEGORIES.find(c => c.value === selectedCategory);

  const renderDropdownContent = () => {
    switch (view) {
      case 'menu':
        return (
          <>
            <button className="action-item action-spotlight" onClick={() => { onSpotlightClick(profile); handleClose(); }}>
              <FaStar /><span>Spotlight</span>
            </button>
            <button className="action-item action-buy" onClick={() => { onBuyClick(profile); handleClose(); }}>
              <FaShoppingCart /><span>Buy</span>
            </button>
            <div className="action-divider" />
            <button className="action-item action-share" onClick={handleShare}>
              <FaShare /><span>Share</span>
            </button>
            {!isOwnProfile && (
              <>
                <div className="action-divider" />
                <button className="action-item action-report" onClick={() => setView('report-category')}>
                  <FaFlag /><span>Report</span><FaChevronRight className="action-chevron" />
                </button>
                <button className="action-item action-block" onClick={() => setView('block-confirm')}>
                  <FaBan /><span>Block</span>
                </button>
              </>
            )}
          </>
        );

      case 'report-category':
        return (
          <div className="action-subpanel">
            <div className="action-subpanel-header">
              <button className="action-back" onClick={() => setView('menu')}><FaArrowLeft /></button>
              <span>Report {formatName({ displayName: profile.creatorDisplayName, username: profile.creatorUsername })}</span>
            </div>
            <p className="action-subpanel-label">What's the issue?</p>
            {REPORT_CATEGORIES.map(cat => (
              <button
                key={cat.value}
                className="action-item action-subitem"
                onClick={() => {
                  setSelectedCategory(cat.value);
                  setSelectedReason('');
                  setView(cat.subcategories.length > 0 ? 'report-reason' : 'report-details');
                }}
              >
                <span>{cat.label}</span><FaChevronRight className="action-chevron" />
              </button>
            ))}
          </div>
        );

      case 'report-reason':
        return (
          <div className="action-subpanel">
            <div className="action-subpanel-header">
              <button className="action-back" onClick={() => setView('report-category')}><FaArrowLeft /></button>
              <span>{activeCategoryData?.label}</span>
            </div>
            <p className="action-subpanel-label">Select a reason</p>
            {activeCategoryData?.subcategories.map(sub => (
              <button
                key={sub.value}
                className="action-item action-subitem"
                onClick={() => { setSelectedReason(sub.value); setView('report-details'); }}
              >
                <span>{sub.label}</span>
              </button>
            ))}
          </div>
        );

      case 'report-details':
        return (
          <div className="action-subpanel">
            <div className="action-subpanel-header">
              <button className="action-back" onClick={() => setView(activeCategoryData?.subcategories.length ? 'report-reason' : 'report-category')}>
                <FaArrowLeft />
              </button>
              <span>Additional details</span>
            </div>
            <p className="action-subpanel-label">Anything else? <span className="action-optional">(optional)</span></p>
            <textarea
              className="action-textarea"
              placeholder="Describe what happened..."
              value={details}
              onChange={e => setDetails(e.target.value)}
              maxLength={500}
              rows={3}
            />
            <button className="action-submit" onClick={handleReportSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        );

      case 'block-confirm':
        return (
          <div className="action-subpanel">
            <div className="action-subpanel-header">
              <button className="action-back" onClick={() => setView('menu')}><FaArrowLeft /></button>
              <span>Block User</span>
            </div>
            <p className="action-subpanel-label action-subpanel-confirm">
              Block <strong>{formatName({ displayName: profile.creatorDisplayName, username: profile.creatorUsername })}</strong>? They won't be able to interact with you.
            </p>
            <div className="action-confirm-btns">
              <button className="action-cancel" onClick={() => setView('menu')}>Cancel</button>
              <button className="action-submit action-submit--danger" onClick={handleBlock} disabled={isSubmitting}>
                {isSubmitting ? 'Blocking...' : 'Block'}
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="profile-actions-menu" ref={menuRef}>
      <button
        className="actions-menu-trigger"
        onClick={handleToggle}
        aria-label="Profile actions"
        aria-expanded={isOpen}
      >
        <FaEllipsisV />
      </button>

      {isOpen && (
        <div className="actions-menu-dropdown">
          {renderDropdownContent()}
        </div>
      )}
    </div>
  );
};

export default ProfileActionsMenu;
